/**
 * Inline SVG figures (spec §4.9): authored SVG is validated by the content
 * validator and sanitised again here with DOMPurify's SVG profile before it is
 * ever inserted into the document.
 */
import DOMPurify from "dompurify";
import { useMemo } from "react";
import clsx from "clsx";
import type { Figure as FigureData } from "@shared/types";
import { Localized, useLocalizedText } from "./Localized";

export interface FigureProps {
  figure: FigureData;
  className?: string;
}

/** Exported for tests: the exact sanitiser configuration used for content SVG. */
export function sanitizeSvg(svg: string): string {
  return DOMPurify.sanitize(svg, {
    USE_PROFILES: { svg: true, svgFilters: true },
    FORBID_TAGS: ["script", "foreignObject", "a", "use", "image"],
    FORBID_ATTR: ["onload", "onerror", "onclick", "href", "xlink:href"],
  });
}

export function Figure({ figure, className }: FigureProps) {
  const localized = useLocalizedText();
  const alt = localized(figure.alt);
  const html = useMemo(() => sanitizeSvg(figure.svg), [figure.svg]);

  return (
    <figure className={clsx("my-4", className)}>
      <div
        role="img"
        aria-label={alt}
        className="[&>svg]:mx-auto [&>svg]:h-auto [&>svg]:max-w-full text-ink-800 dark:text-ink-100"
        // Sanitised immediately above with the SVG profile.
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {figure.caption && (
        <figcaption className="mt-2 text-center text-sm text-ink-600 dark:text-ink-400">
          <Localized value={figure.caption} variant="compact" />
        </figcaption>
      )}
    </figure>
  );
}
